import type React from "react";
import { useState } from "react";
import {
    BarChart2,
    Bookmark,
    Heart,
    Loader,
    MessageCircle,
    Repeat,
    Share,
} from "lucide-react";
import TwitterOverlay from "./TwitterOverlay";
import { MediaType } from "../../../types/media";
import type { TwitterPostProps } from "../types";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/store";
import useProfileImage from "../../../hooks/useProfileImage";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { calculateFittedFontSize } from "../../../utils/autoFitFont";
import { splitXThread } from "../../../utils/xThreadSplit";
import { composedThreadTweets } from "../../../utils/xComposedThread";
import PostVideoView from "../PostVideoView";

const TwitterPost: React.FC<TwitterPostProps> = ({ post, setLoading, hideDatetime = false, contentCharLimit }) => {
    const isMobile = useIsMobile(900);
    const [overlayOpen, setOverlayOpen] = useState(false);
    const [initialImageIndex, setInitialImageIndex] = useState(0);

    const user = useSelector((state: RootState) => state.auth.user);
    const profileImage = useProfileImage(user) || "";

    // Always normalize media to an array
    const mediaList = Array.isArray(post?.media) ? post?.media : [];

    

    // Filter out just the image media types
    const imageMedia = mediaList?.filter(
        (m) =>
            m.type === MediaType.ListingImage ||
            m.type === MediaType.AssetImage ||
            m.type === MediaType.Promotion
    );

    const handleImageClick = (index: number) => {
        setInitialImageIndex(index);
        setOverlayOpen(true);
    };

    const renderScheduledTime = () => {
        // Don't render time if hideDatetime is true
        if (hideDatetime) {
            return null;
        }
        
        // Show formatted date/time in preview mode
        if (post?.type === 'preview') {
            const scheduledFor = post?.scheduledFor;
            if (!scheduledFor) {
                return "Invalid Date";
            }
            const date = new Date(scheduledFor);
            const dateString = date.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
            });
            const timeString = date.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
            return `${dateString} at ${timeString}`;
        }

        // Show relative time for scheduled/published posts
        const scheduledFor = post?.scheduledFor;
        if (!scheduledFor) {
            console.warn("TwitterPost - scheduledFor is undefined/null for relative time");
            return "Invalid Date";
        }
        
        const scheduled = new Date(scheduledFor);
        const diff = scheduled.getTime() - Date.now();
        const absDiff = Math.abs(diff);
        const minuteMs = 1000 * 60;
        const hourMs = minuteMs * 60;
        const dayMs = hourMs * 24;

        if (absDiff < minuteMs) {
            return diff < 0 ? "0m" : "in 0m";
        } else if (absDiff < hourMs) {
            const minutes = Math.round(absDiff / minuteMs);
            return diff < 0 ? `${minutes}m` : `in ${minutes}m`;
        } else if (absDiff < dayMs) {
            const hours = Math.round(absDiff / hourMs);
            return diff < 0 ? `${hours}h` : `in ${hours}h`;
        } else {
            const days = Math.round(absDiff / dayMs);
            return diff < 0 ? `${days}d` : `in ${days}d`;
        }
    };

    const renderMedia = () => {
        // Check if this is a Lead Generation post (either type "Lead Generation" or preview with styling)
        const isLeadGenerationPost = post?.type === 'Lead Generation' || 
                                    (post?.type === 'preview' && post?.background && post?.textColor);

        // For Lead Generation posts: if image is generated, display it instead of re-rendering styled block
        if (isLeadGenerationPost && mediaList && mediaList.length > 0 && mediaList[0].signedUrl) {
            return (
                <div className="w-full relative mt-2">
                    <img
                        src={mediaList[0].signedUrl}
                        alt="Lead Generation Post"
                        className="object-cover w-full h-full cursor-pointer"
                        style={{ aspectRatio: "1200/675" }}
                    />
                </div>
            );
        }

        // If backgroundColor and textColor are present, render styled content block instead of media
        if (post?.background && post?.textColor) {
            // Use imageText for the graphic if available, otherwise use content
            const graphicText = (post as any).imageText || post.content;
            const content = (
                typeof graphicText === "string"
                    ? graphicText
                    : Array.isArray(graphicText)
                    ? graphicText.join("\n\n")
                    : ""
            ).trim();

            // Helper function to get background style
            const getBackgroundStyle = (bg: any) => {
                if (!bg) return undefined;
                if (bg.type === 'gradient') {
                    return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                }
                return bg.color || '#000000';
            };

            // Platform-specific dimensions (matching backend)
            const platformDimensions = { width: 1200, height: 675 }; // Twitter/X dimensions
            const maxWidth = 500;
            const scale = maxWidth / platformDimensions.width;
            const displayWidth = platformDimensions.width * scale;
            const displayHeight = platformDimensions.height * scale;

            if (isMobile) {
                // Mobile: fluid layout — fills container width, locked to 16:9 aspect ratio
                const mobileFontSize = calculateFittedFontSize(content, post.fontSize || 16, post.fontFamily, 320, 180);
                return (
                    <div
                        className="w-full flex items-center justify-center rounded-lg mt-2"
                        style={{
                            aspectRatio: "16/9",
                            background: getBackgroundStyle(post.background),
                            color: post.textColor,
                            fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                            padding: "8%",
                        }}
                    >
                        <p
                            className="font-bold text-center break-words"
                            style={{
                                fontSize: `${mobileFontSize}px`,
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                width: "100%",
                            }}
                        >
                            {content}
                        </p>
                    </div>
                );
            }

            // Desktop: original pixel-based rendering (unchanged)
            const baseFontSize = post.fontSize || 16;
            const fittedFontSize = calculateFittedFontSize(content, baseFontSize, post.fontFamily, displayWidth, displayHeight);
            const horizontalPadding = Math.round(displayWidth * 0.08);
            const verticalPadding = Math.round(displayHeight * 0.08);

            return (
                <div className="w-full flex justify-center mt-2">
                    <div className="flex items-center justify-center rounded-lg"
                         style={{ 
                             background: getBackgroundStyle(post.background), 
                             color: post.textColor,
                             fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                             width: `${displayWidth}px`,
                             height: `${displayHeight}px`,
                             paddingLeft: `${horizontalPadding}px`,
                             paddingRight: `${horizontalPadding}px`,
                             paddingTop: `${verticalPadding}px`,
                             paddingBottom: `${verticalPadding}px`,
                         }}>
                        <p className="font-bold text-center"
                           style={{
                               fontSize: `${fittedFontSize}px`,
                               lineHeight: 1.5,
                           }}>
                            {content}
                        </p>
                    </div>
                </div>
            );
        }

        if (mediaList?.length === 0) return null;

        const allItems = mediaList || [];
        const total = allItems.length;

        // Renders the content of a single grid cell.
        // The parent wrapper must be `position: relative` with a defined height (via aspect-ratio).
        const renderCellContent = (item: (typeof allItems)[0], displayIndex: number) => {
            const isVideo =
                item.type === MediaType.ListingVideo ||
                item.type === MediaType.AssetVideo;

            if (isVideo) {
                if (item.isProcessing) {
                    return (
                        <>
                            {item.thumbnail ? (
                                <img
                                    src={item.thumbnail}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            ) : (
                                <div className="absolute inset-0 bg-black" />
                            )}
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center gap-2 rounded">
                                <Loader className="animate-spin w-4 h-4 text-white" />
                                <span className="text-white text-xs font-medium">
                                    Video Processing
                                </span>
                            </div>
                        </>
                    );
                }
                return (
                    <video
                        muted
                        src={item.signedUrl || item.url || ""}
                        controls
                        className="absolute inset-0 w-full h-full object-contain bg-black"
                    />
                );
            }

            // Image — find its index in imageMedia for the overlay
            const imgIdx = imageMedia?.findIndex((m) => m.id === item.id) ?? displayIndex;
            return (
                <img
                    src={item.signedUrl || item.url || profileImage}
                    alt={`Media ${displayIndex + 1}`}
                    className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                    onClick={() => handleImageClick(imgIdx >= 0 ? imgIdx : 0)}
                />
            );
        };

        // ── Single item ──────────────────────────────────────────────────────────
        if (total === 1) {
            const item = allItems[0];
            const isVideo =
                item.type === MediaType.ListingVideo ||
                item.type === MediaType.AssetVideo;

            if (isVideo) {
                return (
                    <div className="mt-2">
                        <PostVideoView
                            src={item.signedUrl || item.url || ""}
                            metadata={item.videoMetadata}
                            thumbnail={item.thumbnail}
                            isProcessing={item.isProcessing}
                            autoPlay={isMobile}
                            className="rounded"
                        />
                    </div>
                );
            }

            return (
                <div className="w-full relative mt-2">
                    <img
                        src={item.signedUrl || item.url || profileImage}
                        alt="Media 1"
                        className="object-cover w-full cursor-pointer"
                        style={{ aspectRatio: "1200/675" }}
                        onClick={() => handleImageClick(0)}
                    />
                </div>
            );
        }

        // ── 2 items: side-by-side ────────────────────────────────────────────────
        if (total === 2) {
            return (
                <div className="grid grid-cols-2 gap-1 mt-2">
                    {allItems.slice(0, 2).map((item, i) => (
                        <div
                            key={i}
                            className="relative overflow-hidden"
                            style={{ aspectRatio: "700/800" }}
                        >
                            {renderCellContent(item, i)}
                        </div>
                    ))}
                </div>
            );
        }

        // ── 3 items: large left + two stacked right ──────────────────────────────
        if (total === 3) {
            return (
                <div className="grid grid-cols-2 gap-1 mt-2">
                    <div
                        className="row-span-2 relative overflow-hidden"
                        style={{ aspectRatio: "7/8" }}
                    >
                        {renderCellContent(allItems[0], 0)}
                    </div>
                    {allItems.slice(1, 3).map((item, i) => (
                        <div
                            key={i + 1}
                            className="relative overflow-hidden"
                            style={{ aspectRatio: "7/4" }}
                        >
                            {renderCellContent(item, i + 1)}
                        </div>
                    ))}
                </div>
            );
        }

        // ── 4 or more: 2×2 grid ──────────────────────────────────────────────────
        const extra = total - 4;
        return (
            <div className="grid grid-cols-2 gap-1 mt-2">
                {allItems.slice(0, 4).map((item, i) => (
                    <div
                        key={i}
                        className="relative overflow-hidden"
                        style={{ aspectRatio: "2/1" }}
                    >
                        {renderCellContent(item, i)}
                        {i === 3 && extra > 0 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer">
                                <span className="text-white text-xl font-semibold">
                                    +{extra}
                                </span>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const rawContent = post?.content || "";
    const rawContentStr = Array.isArray(rawContent) ? rawContent.join("\n") : rawContent;

    // Thread ALL content — no pre-splitting at platform limit.
    // Overflow styling is applied per-thread via cumulative char tracking below.
    const fullText =
        post?.footerType === "in-post" && post?.includeFooter && post?.footer
            ? `${rawContentStr}\n\n${post?.footer}`
            : rawContentStr;

    // A thread the v2 writer composed keeps its own breaks and an in-post footer is its own last tweet,
    // as the publisher sends it; anything else is packed as before.
    const inPostFooter =
        post?.footerType === "in-post" && post?.includeFooter && post?.footer ? post.footer : "";
    const threads = post?.threadComposed
        ? composedThreadTweets(rawContentStr, inPostFooter)
        : splitXThread(
              Array.isArray(fullText) ? fullText.join("\n") : fullText
          );

    // Helper: render a single thread's text with overflow styling.
    // `limitStart` = how many chars from the start of this thread are within the platform limit.
    //   Infinity  → no limit applies, render entire thread normally
    //   >= thread.length → entire thread within limit, render normally
    //   <= 0      → entire thread past the limit, render all in red strikethrough
    //   in between → thread straddles the limit, split and style accordingly
    const renderThreadText = (thread: string, limitStart: number): React.ReactNode => {
        if (!contentCharLimit || limitStart === Infinity || limitStart >= thread.length) {
            // Entire thread within limit — render normally
            return (
                <>
                    {thread.split("\n\n").map((para, pi) => (
                        <div key={pi} className={pi > 0 ? "mt-4" : ""}>
                            {para.split("\n").map((line, li) => (
                                <p key={`${pi}-${li}`}>{line}</p>
                            ))}
                        </div>
                    ))}
                </>
            );
        }
        if (limitStart <= 0) {
            // Entire thread past the limit
            return (
                <span className="bg-red-100 line-through text-red-600">{thread}</span>
            );
        }
        // Thread straddles the limit — split and style the overflow portion
        const normalPart = thread.substring(0, limitStart);
        const overflowPart = thread.substring(limitStart);
        return (
            <>
                {normalPart.split("\n\n").map((para, pi) => (
                    <div key={pi} className={pi > 0 ? "mt-4" : ""}>
                        {para.split("\n").map((line, li) => (
                            <p key={`${pi}-${li}`}>{line}</p>
                        ))}
                    </div>
                ))}
                <span className="bg-red-100 line-through text-red-600">{overflowPart}</span>
            </>
        );
    };

    const isLeadGenerationPost =
    post?.type === 'Lead Generation' ||
    (post?.type === 'preview' && post?.background && post?.textColor);

    return (
        <div>
            {/* Main Tweet */}
            <div className="max-w-xl mx-auto bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-3">
                    <div className="flex items-start gap-2">
                        {/* Avatar — shrinks on mobile */}
                        <img
                            src={post?.userImage || profileImage}
                            alt="Profile"
                            className={`rounded-full object-cover flex-shrink-0 ${isMobile ? 'w-7 h-7' : 'w-10 h-10'}`}
                            onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                            }}
                        />
                        <span 
                            className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full flex-shrink-0"
                            style={{ display: 'none' }}
                        >
                            {post?.displayName 
                                ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                : 'U'}
                        </span>
                        <div className="flex-1 min-w-0">
                            {/* Header: name · handle · time — all truncate on mobile */}
                            <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                <span className={`font-bold text-gray-900 truncate flex-shrink min-w-0 ${isMobile ? 'text-xs max-w-[80px]' : ''}`}>
                                    {post?.displayName}
                                </span>
                                <span className={`text-gray-500 truncate flex-shrink min-w-0 ${isMobile ? 'text-xs max-w-[80px]' : ''}`}>
                                    @{post?.username}
                                </span>
                                <span className="text-gray-500 flex-shrink-0">·</span>
                                <span className={`text-gray-500 flex-shrink-0 whitespace-nowrap ${isMobile ? 'text-xs' : ''}`}>
                                    {renderScheduledTime()}
                                </span>
                            </div>
                            <div className="mt-1 text-gray-900 break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
                                !post?.content &&
                                (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
                                    <div className="space-y-2 py-1">
                                        <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
                                        <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
                                    </div>
                                ) : (
                                    /* Don't render content text if using background colors (it's rendered in renderMedia) */
                                    renderThreadText(threads[0] ?? "", contentCharLimit ? contentCharLimit : Infinity)
                                )}
                            </div>
                            {renderMedia()}
                            {/* Action icons row */}
                            <div className={`mt-3 flex items-center justify-between text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                <div className={`flex items-center ${isMobile ? 'gap-0.5' : 'gap-1'}`}>
                                    <MessageCircle className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                                    {!isMobile && <span>10</span>}
                                </div>
                                <div className={`flex items-center ${isMobile ? 'gap-0.5' : 'gap-1'}`}>
                                    <Repeat className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                                    {!isMobile && <span>3</span>}
                                </div>
                                <div className={`flex items-center ${isMobile ? 'gap-0.5' : 'gap-1'}`}>
                                    <Heart className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                                    {!isMobile && <span>132</span>}
                                </div>
                                <div className={`flex items-center ${isMobile ? 'gap-0.5' : 'gap-1'}`}>
                                    <BarChart2 className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                                    {!isMobile && <span>560</span>}
                                </div>
                                <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-3'}`}>
                                    <Bookmark className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                                    <Share className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Threaded Replies */}
            {threads.length > 1 && (
                <div className="relative max-w-xl mx-auto mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="max-w-xl mx-auto mt-4 space-y-2 p-4">
                        {(() => {
                            // Track cumulative chars across all threads to know where the limit falls
                            let cumulative = threads[0]?.length ?? 0;
                            return threads.slice(1).map((thread, idx) => {
                                const threadStart = cumulative;
                                cumulative += thread.length;
                                // How many chars into THIS thread before we hit the limit
                                const limitStart = contentCharLimit ? Math.max(0, contentCharLimit - threadStart) : Infinity;
                                return { thread, idx, limitStart };
                            });
                        })().map(({ thread, idx, limitStart }) => (
                            <div
                                key={idx}
                                className="grid grid-cols-[3rem,1fr] gap-3 relative"
                            >
                                <div className="flex flex-col items-center">
                                    <img
                                        src={post?.userImage || profileImage}
                                        alt="Profile"
                                        className="w-10 h-10 rounded-full object-cover"
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            const fallback = target.nextElementSibling as HTMLElement;
                                            if (fallback) fallback.style.display = 'flex';
                                        }}
                                    />
                                    <span
                                        className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                                        style={{ display: 'none' }}
                                    >
                                        {post?.displayName
                                            ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                            : 'U'}
                                    </span>
                                    {idx !== threads.slice(1).length - 1 && (
                                        <div className="flex-1 w-px bg-gray-300 mt-2" />
                                    )}
                                </div>
                                <div className="min-w-0 overflow-hidden">
                                    <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                        <span className={`font-bold text-gray-900 truncate flex-shrink min-w-0 ${isMobile ? 'text-xs max-w-[80px]' : ''}`}>
                                            {post?.displayName}
                                        </span>
                                        <span className={`text-gray-500 truncate flex-shrink min-w-0 ${isMobile ? 'text-xs max-w-[80px]' : ''}`}>
                                            @{post?.username}
                                        </span>
                                        <span className="text-gray-500 flex-shrink-0">·</span>
                                        <span className={`text-gray-500 flex-shrink-0 whitespace-nowrap ${isMobile ? 'text-xs' : ''}`}>
                                            {renderScheduledTime()}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-gray-900 break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                        {renderThreadText(thread, limitStart)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Comment Footer */}
            {!isLeadGenerationPost && post?.footerType === "comment" && post?.includeFooter && post?.footer && (
                <div className="max-w-xl mx-auto mt-4 bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="p-4">
                        <div className="flex items-start gap-3">
                            <img
                                src={post?.userImage || profileImage || '/placeholder-avatar.svg'}
                                alt="Profile"
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallback = target.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                }}
                            />
                            <span 
                                className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                                style={{ display: (post?.userImage || profileImage) ? 'none' : 'flex' }}
                            >
                                {post?.displayName 
                                    ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                    : 'U'}
                            </span>
                            <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                                    <span className={`font-bold text-gray-900 truncate flex-shrink min-w-0 ${isMobile ? 'text-xs max-w-[80px]' : ''}`}>
                                        {post?.displayName}
                                    </span>
                                    <span className={`text-gray-500 truncate flex-shrink min-w-0 ${isMobile ? 'text-xs max-w-[80px]' : ''}`}>
                                        @{post?.username}
                                    </span>
                                    <span className="text-gray-500 flex-shrink-0">·</span>
                                    <span className={`text-gray-500 flex-shrink-0 whitespace-nowrap ${isMobile ? 'text-xs' : ''}`}>
                                        {renderScheduledTime()}
                                    </span>
                                </div>
                                <div className="mt-1 text-gray-900 break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                    {post?.footer
                                        .split("\n\n")
                                        .map((para, pi) => (
                                            <div
                                                key={pi}
                                                className={pi > 0 ? "mt-4" : ""}
                                            >
                                                {para
                                                    .split("\n")
                                                    .map((line, li) => (
                                                        <p key={`${pi}-${li}`}>
                                                            {line}
                                                        </p>
                                                    ))}
                                            </div>
                                        ))}
                                </div>
                                <div className="mt-3 flex items-center gap-6 text-gray-500 text-sm">
                                    <div className="flex items-center gap-1">
                                        <Heart className="w-4 h-4" />
                                        <span>0</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <BarChart2 className="w-4 h-4" />
                                        <span>0</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Share className="w-4 h-4" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay */}
            {imageMedia && imageMedia?.length > 0 && (
                <TwitterOverlay
                    images={imageMedia || []}
                    initialIndex={initialImageIndex}
                    isOpen={overlayOpen}
                    onClose={() => setOverlayOpen(false)}
                    post={post}
                />
            )}
        </div>
    );
};

export default TwitterPost;
